import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Select, Store } from "@ngxs/store";
import { ApplicationStateService } from "app/shared/security/application-state.service";
import { Map } from "immutable";
import { Observable, Subject } from "rxjs";
import { filter, map, takeUntil, tap } from "rxjs/operators";
import { AuthUser } from "~app/core/auth/core/auth-user.model";
import { AuthState } from "~app/core/auth/core/auth.state";
import { SubscriptionState } from "~app/farp-odq/store/subscription";
import { LoadMemberId } from "~app/farp-odq/store/subscription/subscription.actions";
import { CartActions } from "../../shopping-cart/cart";
import { AnnualDeclarationFormCollection } from "../form/annual-declaration-form/shared/model/annual-declaration.model";
import {
    GenerateAnnualDeclarationPdf,
    LoadCompanyOtherEstablishmentFirebase,
    SelectLegalEntity,
} from "../form/annual-declaration-form/shared/state/annual-declaration.actions";
import { AnnualDeclarationState } from "../form/annual-declaration-form/shared/state/annual-declaration.state";
import { LegalEntityPracticeState } from "../register-report/store/legalEntityPractice/legalEntityPractice.state";
import { CompanySelector, FormStatus } from "../shared/model/company.model";
import { LegalEntityFirebaseService } from "../shared/service/legal-entity-firebase-service";
import {
    LoadInvoices,
    SeeLegalEntityInvoice,
} from "./store/legalEntityPracticeV3Invoices/legalEntityPracticeV3Invoices.action";
import { LegalEntityPracticeV3InvoicesState } from "./store/legalEntityPracticeV3Invoices/legalEntityPracticeV3Invoices.state";
import { Invoice } from "./store/legalEntityPracticeV3Invoices/legalEntityPracticeV3Invoices.state.model";

@Component({
    selector: "odq-form-state",
    templateUrl: "./form-state.component.html",
    styleUrls: ["./form-state.component.scss"],
    standalone: false
})
export class FormStateComponent implements OnInit, OnDestroy {
  @Input() legalEntity: CompanySelector;

  @Select(LegalEntityPracticeV3InvoicesState.invoices)
  legalEntityInvoices$: Observable<Map<string, Invoice[]>>;

  @Select(LegalEntityPracticeV3InvoicesState.loading)
  legalEntityInvoiceLoading$: Observable<Map<string, boolean[]>>;

  formCollections$ = this.store.select<
    Map<string, AnnualDeclarationFormCollection>
  >(AnnualDeclarationState.formCollections);

  legalEntityInvoices: Invoice[] = [];
  legalEntityRegisterReportDownloading$ = this.store.select<boolean>(
    LegalEntityPracticeState.loading
  );

  isRecentlyCreatedLegalEntity$: Observable<boolean>;

  user$ = this.store.select<AuthUser>(AuthState.user);
  year$ = this.store.select<number>(AnnualDeclarationState.year);
  memberId$ = this.store.select<string>(SubscriptionState.memberId);
  memberId: string;

  hoverColor = "inherit";
  formStateEnum = FormStatus;
  destroy$ = new Subject<void>();
  isAnnualDeclarationCompleted = false;
  isDeclarationDatePassed = false;

  annualFormClosingMessage$: Observable<SafeHtml>;

  formStatusEnum = FormStatus;
  formState$: Observable<FormStatus>;
  formState: FormStatus;

  isFormEditable$: Observable<boolean>;
  isFormEditable: boolean;
  isSummaryDownloading$: Observable<boolean>;
  isSummaryDownloading: boolean;
  isFormCompleted$: Observable<boolean>;
  isFormCompleted: boolean;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private applicationStateService: ApplicationStateService,
    private legalEntityFirebaseService: LegalEntityFirebaseService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    private store: Store
  ) {}

  ngOnInit() {
    this.isRecentlyCreatedLegalEntity$ = this.legalEntityFirebaseService.isRecentlyCreatedLegalEntity(this.legalEntity.id);

    this.user$.pipe(takeUntil(this.destroy$)).subscribe((u) => {
      this.store.dispatch(new LoadMemberId(u.permitNumber));
    });

    this.memberId$
      .pipe(
        takeUntil(this.destroy$),
        tap((m) => {
          if (m) {
            this.memberId = m;
            this.store.dispatch(
              new LoadInvoices(
                this.legalEntity.legalEntityNumber,
                this.memberId
              )
            );
          }
        })
      )
      .subscribe();

    this.formState$ = this.formCollections$.pipe(
      map((cols) => cols.get(this.legalEntity.id)?.state),
      takeUntil(this.destroy$)
    );

    this.isSummaryDownloading$ = this.formCollections$.pipe(
      map((cols) => cols.get(this.legalEntity.id)?.isSummaryDownloading),
      takeUntil(this.destroy$)
    );

    this.isFormEditable$ = this.formState$.pipe(
      map(
        (state) =>
          state === FormStatus.Initial || state === FormStatus.InProgress
      ),
      takeUntil(this.destroy$)
    );

    this.isFormCompleted$ = this.formState$.pipe(
      map((state) => state === FormStatus.Completed),
      takeUntil(this.destroy$)
    );

    this.applicationStateService
      .getAnnualFormClosingDate()
      .pipe(takeUntil(this.destroy$))
      .subscribe((closingDate) => {
        this.isDeclarationDatePassed = !!closingDate
          ? new Date(closingDate).getTime() < new Date().getTime()
          : false;
      });
    this.annualFormClosingMessage$ = this.applicationStateService
      .getAnnualFormClosingMessage()
      .pipe(
        map((message) =>
          this.sanitizer.bypassSecurityTrustHtml(
            message[this.translate.currentLang]
          )
        ),
        takeUntil(this.destroy$)
      );

    this.legalEntityInvoices$
      .pipe(
        filter((res) => !!res),
        takeUntil(this.destroy$),
        map((allLegalEntityInvoices) => {
          const legalEntityInvoices = allLegalEntityInvoices.get(
            this.legalEntity.legalEntityNumber
          );
          this.legalEntityInvoices = legalEntityInvoices?.filter((invoice) => {
            return (
              new Date(invoice?.date)?.getFullYear() ===
              new Date().getFullYear()
            );
          });
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goToForm() {
    this.store.dispatch(new CartActions.ClearCart());
    
    this.store.dispatch(new SelectLegalEntity(this.legalEntity.id));
    this.store.dispatch(
      new LoadCompanyOtherEstablishmentFirebase(this.legalEntity.id)
    );
    this.router.navigate(
      [
        `/company/${
          this.legalEntity.legalEntityNumber
        }/form/annual/${new Date().getFullYear()}`,
      ],
      { relativeTo: this.route }
    );
  }

  getPdf() {
    this.store.dispatch(new GenerateAnnualDeclarationPdf(this.legalEntity.id));
  }

  isFormOpened() {
    return !this.isDeclarationDatePassed;
  }

  showInvoice(invoice: Invoice) {
    this.store.dispatch(
      new SeeLegalEntityInvoice(
        invoice.id,
        invoice.number,
        this.memberId,
        this.legalEntity.legalEntityNumber
      )
    );
  }
}
